import { Button, Input, Spin } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { BiCopy, BiEdit } from "react-icons/bi";
import { MdDeleteOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { DataTable } from "@/components";
import { userRole, userRoleFormate } from "@/constant";

import debounce from "lodash/debounce";
import { FaPlus } from "react-icons/fa";
import MetaCustomReportTemplateModal from "./redesign/MetaCustomReportTemplateModal";
import { flare } from "@flareapp/js";

const ReportTemplateList = () => {
  const { business, userDetails } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 10);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLimit(Number(searchParams.get("limit")) || 10);
    setPage(Number(searchParams.get("page")) || 1);
  }, [searchParams]);

  const getTemplateList = debounce(async (query) => {
    try {
      if (!isLoading) {
        setIsLoading(true);
        const response = await api.get(endpoint.get_customReport_template_list, {
          withCredentials: true,
          params: {
            limit: limit / 2,
            page,
            ...query,
          },
        });
        const myData = await api.get(endpoint.my_customReport_template, {
          params: {
            limit: limit / 2,
            page,
            ...query,
          },
        });
        setData([...(response?.data || []), ...myData.data]);
        setTotal(Number(response.count) + Number(myData.count));
      }
    } catch (e) {
      Swal.fire({
        title: "Ooops",
        text: e.message,
        icon: "danger",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  }, 1000);

  useEffect(() => {
    getTemplateList();
  }, [page, limit, business]);

  useEffect(() => {
    document.title = `${appConfig.name} - Report Template List`;
  }, []);

  const columns = [
    {
      title: "No.",
      // width: "60px",
      render: (_, __, index) => <div>{(page - 1) * limit + index + 1}</div>,
    },
    {
      title: `${userDetails.role !== userRole?.superAdmin ? "Report " : ""} Template Name`,
      render: (row) => row.name,
    },
    {
      title: "Description",
      render: (row) =>
        row.description ? (
          <div title={row.description}>
            {row.description?.length > 50 ? `${row.description.slice(0, 50)}...` : row.description}
          </div>
        ) : (
          "--"
        ),
    },
    {
      title: "Date Generated",
      render: (row) => moment(row.createdAt).format("DD/MM/YYYY HH:mm:ss"),
      align: "center",
    },
    {
      title: "Created By",
      render: (row) => (row?.user?.name ? row?.user?.name : "--"),
      maxWidth: "100px",
    },
    {
      title: "User Type",
      render: (row) => (row?.user?.role ? userRoleFormate[row?.user?.role] : "--"),
      maxWidth: "100px",
    },
    userDetails.role === userRole?.superAdmin && {
      title: "Business",
      render: (row) => (row?.business?.name ? `${row?.business?.name}(${row?.business?.id})` : "--"),
      maxWidth: "100px",
    },
    {
      title: "Action",
      width: "280px",
      align: "center",
      render: (row) => (
        <div>
          <Link to={`/report-template/${row._id}`}>
            <button className="btn btn-az-primary  me-1">
              <AiOutlineEye className="text-white" />
            </button>
          </Link>
          {/* EditReport  */}
          {(row?.user?.role === userDetails?.role || userDetails?.role === userRole?.superAdmin) && (
            <Link to={`/edit-report-template/${row._id}`}>
              <button className="btn btn-edit me-1 d-none d-md-inline">
                <BiEdit />
              </button>
            </Link>
          )}
          <button
            className="btn btn-view-gray me-1 d-none d-md-inline"
            title="Copy Report Template"
            onClick={() => copyHandler(row._id)}
          >
            <BiCopy />
          </button>
          {(row?.user?.role === userDetails?.role || userDetails?.role === userRole?.superAdmin) && (
            <button className="me-2 btn btn-danger" onClick={() => deleteHandler(row._id)}>
              <MdDeleteOutline className="text-white" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const copyHandler = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "This action will duplicate the existing template",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          navigate(`/edit-report-template/${id}?cp=true`);
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // handler
  const deleteHandler = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you wish to delete this template?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await axios.delete(`${endpoint.remove_customReport_template}/${id}`, {
            withCredentials: true,
          });
          if (response.status === 200) {
            Swal.fire("Deleted!", "Report Template Deleted Successfully!", "success");
            getTemplateList();
            // getActiveReport();
          }
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <div className="px-md-3 mt-3">
        <div className="border  p-3">
          {/* <h5>Report List</h5> */}
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <label className="az-content-label">Manage Report Template</label>
              <span className="d-block py-2">Report Template List</span>
            </div>
            <div className="col-md-4 d-none d-md-flex justify-content-end p-0">
              <Link to="/report-template-list?mode=create">
                <Button icon={<FaPlus />} type="primary">
                  Create Report Template
                </Button>
              </Link>
            </div>
          </div>
          <hr className="mt-0 mb-5" />

          <div>
            <div className="d-flex justify-content-end mb-3">
              <Input
                type="search"
                size="large"
                style={{
                  width: 200,
                }}
                onChange={(e) => {
                  getTemplateList({ name: e.target.value });
                  setPage(1);
                }}
                placeholder="Search"
                // className="me-2"
                aria-label="Search"
              />
            </div>

            <DataTable
              columns={columns}
              dataSource={data}
              pagination={{
                pageSize: limit,
                current: page,
                total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                pageSizeOptions: [10, 20, 50],
                responsive: true,
              }}
              onChange={(pagination) =>
                navigate(`/report-template-list?page=${pagination.current}&limit=${pagination.pageSize}`)
              }
            />
          </div>
        </div>

        <MetaCustomReportTemplateModal
          {...{
            show: searchParams.get("mode") === "create",
            setShow: () => {},
            mode: "create",
            data: {},
            getData: getTemplateList,
          }}
        />
      </div>
    </Spin>
  );
};

export default ReportTemplateList;
